import { getApiBaseUrl } from "../config/env";

const SESSION_KEY = "wedding_admin_session_v1";

/** @returns {boolean} */
export function isAdminAuthLive() {
  return process.env.REACT_APP_ADMIN_AUTH === "live";
}

function apiPath(path) {
  const base = getApiBaseUrl();
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * POST /api/admin/login — wire when backend exists.
 * Body: { email, password }
 * Expected JSON: { user: { id, email, role, displayName?, mustChangePassword?, ... } }
 * Prefer httpOnly session cookie from API; avoid storing long-lived tokens in localStorage.
 *
 * @param {{ email: string; password: string }} credentials
 * @returns {Promise<{ user: { id: string; email: string; role: string; displayName?: string; mustChangePassword?: boolean } }>}
 */
export async function requestAdminLogin(credentials) {
  const email = credentials.email?.trim();
  const { password } = credentials;
  if (!email || !password) {
    throw new Error("Email and password are required.");
  }

  if (!isAdminAuthLive()) {
    await new Promise((r) => setTimeout(r, 350));
    return {
      user: {
        id: "dev-admin",
        email,
        role: "admin",
        displayName: "Administrator",
        mustChangePassword: false,
      },
    };
  }

  const res = await fetch(apiPath("/api/admin/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    let message = "Sign in failed.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const data = await res.json();
  if (!data.user || data.user.role !== "admin") {
    throw new Error("Not an administrator account.");
  }
  return { user: data.user };
}

/** @returns {Promise<{ user: Record<string, unknown> }>} */
export async function requestAdminMe() {
  const res = await fetch(apiPath("/api/admin/me"), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    const err = new Error("Session expired.");
    throw err;
  }
  const data = await res.json();
  if (!data.user || data.user.role !== "admin") {
    throw new Error("Not an administrator account.");
  }
  return { user: data.user };
}

export async function requestAdminLogout() {
  if (!isAdminAuthLive()) return;
  try {
    await fetch(apiPath("/api/admin/logout"), {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
}

/** @param {{ currentPassword: string; newPassword: string }} body */
export async function requestAdminChangePassword(body) {
  const { currentPassword, newPassword } = body;
  if (!currentPassword || !newPassword) {
    throw new Error("All password fields are required.");
  }
  const res = await fetch(apiPath("/api/admin/change-password"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    let message = "Could not update password.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const data = await res.json();
  if (!data.user || data.user.role !== "admin") {
    throw new Error("Unexpected response.");
  }
  return { user: data.user };
}

export function persistAdminSession(user) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user, savedAt: Date.now() }));
}

export function readAdminSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user ?? null;
  } catch {
    return null;
  }
}

export function clearAdminSession() {
  sessionStorage.removeItem(SESSION_KEY);
}
